import React, { useState } from 'react';
import styled from 'styled-components';
import { getResponsiveValue } from '../../styles/breakpoints';
import { Ionicons } from '@expo/vector-icons';

const DEFAULT_PROFILE_PHOTOS = [
  require('../../../assets/profile_photos/koi.png'),
  require('../../../assets/profile_photos/bear.png'),
];

export function MessageCard({ userAvatar, username, message, timestamp, isCurrentUser, onEdit, onDelete, messageId, photoURL, isAnonymous }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(message);
  const [isHovered, setIsHovered] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRandomDefaultPhoto = () => {
    const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
    return DEFAULT_PROFILE_PHOTOS[randomIndex];
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedMessage(message); // Reset to original message when starting edit
  };

  const handleSave = () => {
    if (editedMessage.trim() !== '') {
      onEdit(messageId, editedMessage);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedMessage(message);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      onDelete(messageId);
    }
  };

  const togglePhotoModal = () => {
    setIsPhotoModalOpen(!isPhotoModalOpen);
  };

  return (
    <CardContainer 
      isCurrentUser={isCurrentUser}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <UserInfo>
        <UserAvatar 
          src={userAvatar || getRandomDefaultPhoto()} 
          alt={`${username}'s avatar`} 
          loading="lazy" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = getRandomDefaultPhoto();
          }}
        />
        <UserDetails>
          <Username>{isAnonymous ? 'Anonymous' : username}</Username>
          <TimeStamp>{formatDate(timestamp)}</TimeStamp>
        </UserDetails>
        {isCurrentUser && isHovered && !isEditing && (
          <ActionButtons>
            <ActionButton className="edit-button" onClick={handleEdit} title="Edit message">
              <EditIcon>✎</EditIcon>
            </ActionButton>
            <ActionButton className="delete-button" onClick={handleDelete} title="Delete message">
              <DeleteIcon>
                <Ionicons size={18} name="trash-outline" />
              </DeleteIcon>
            </ActionButton>
          </ActionButtons>
        )}
      </UserInfo>
      {isEditing ? (
        <EditContainer>
          <EditInput
            value={editedMessage}
            onChange={(e) => setEditedMessage(e.target.value)}
            placeholder="Edit your message..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
          />
          <EditButtons>
            <SaveButton onClick={handleSave}>Save</SaveButton>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          </EditButtons>
        </EditContainer>
      ) : (
        <MessageContent>
          <MessageText>{message}</MessageText>
          {photoURL && (
            <MessagePhotoContainer>
              <MessagePhoto 
                src={photoURL} 
                alt="Attached photo" 
                onClick={togglePhotoModal}
              />
            </MessagePhotoContainer>
          )}
        </MessageContent>
      )}

      {isPhotoModalOpen && photoURL && (
        <PhotoModal onClick={togglePhotoModal}>
          <PhotoModalContent onClick={(e) => e.stopPropagation()}>
            <FullSizePhoto src={photoURL} alt="Full size photo" />
            <CloseModalButton onClick={togglePhotoModal}>×</CloseModalButton>
          </PhotoModalContent>
        </PhotoModal>
      )}
    </CardContainer>
  );
}

const CardContainer = styled.div`
  border-radius: 10px;
  display: flex;
  width: ${getResponsiveValue({
    mobile: '95%',
    tablet: '90%',
    laptop: '90%',
    desktop: '90%'
  })};
  max-width: ${getResponsiveValue({
    mobile: '95%',
    tablet: '90%',
    laptop: '90%',
    desktop: '90%'
  })};
  flex-direction: column;
  justify-content: center;
  padding: ${getResponsiveValue({
    mobile: '10px',
    tablet: '10px',
    laptop: '15px',
    desktop: '15px'
  })};
  margin-bottom: 15px;
  border: 2px solid #dfffdd;
  position: relative;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${getResponsiveValue({
    mobile: '5px',
    tablet: '5px',
    laptop: '8px',
    desktop: '8px'
  })};
  margin-bottom: ${getResponsiveValue({
    mobile: '0px',
    tablet: '15px',
    laptop: '15px',
    desktop: '15px'
  })};
`;

const UserAvatar = styled.img`
  width: ${getResponsiveValue({
    mobile: '20px',
    tablet: '25px',
    laptop: '30px',
    desktop: '30px'
  })};
  height: ${getResponsiveValue({
    mobile: '20px',
    tablet: '25px',
    laptop: '30px',
    desktop: '30px'
  })};
  border-radius: 50%;
  object-fit: cover;
  background-color: rgba(255, 255, 255, 0.1);
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: 'row';
  gap: ${getResponsiveValue({
    mobile: '10px',
    tablet: '4px',
    laptop: '4px',
    desktop: '4px'
  })};
  align-items: 'center';
  flex: 1;
`;

const Username = styled.div`
  font-family: 'Computer Says No';
  font-weight: 500;
  font-size: ${getResponsiveValue({
    mobile: '22px',
    tablet: '26px',
    laptop: '26px',
    desktop: '26px'
  })};
  color: "#000000";
  text-shadow: ${getResponsiveValue({
    mobile: '0px 1px 2px rgba(0, 0, 0, 0.25)',
    tablet: '0px 2px 4px rgba(0, 0, 0, 0.25)',
    laptop: '0px 2px 4px rgba(0, 0, 0, 0.25)',
    desktop: '0px 2px 4px rgba(0, 0, 0, 0.25)'
  })};
`;

const TimeStamp = styled.div`
  font-family: 'Computer Says No';
  font-weight: 400;
  font-size: ${getResponsiveValue({
    mobile: '14px',
    tablet: '14px',
    laptop: '18px',
    desktop: '20px'
  })};
  color: "#000000";
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${getResponsiveValue({
    mobile: '42px',
    tablet: '55px',
    laptop: '55px',
    desktop: '55px'
  })};
`;

const MessageText = styled.div`
  font-family: 'Computer Says No';
  font-weight: 400;
  font-size: ${getResponsiveValue({
    mobile: '22px',
    tablet: '26px',
    laptop: '26px',
    desktop: '26px'
  })};
  line-height: 1.4;
  color: "#000000";
  margin-bottom: ${props => props.hasPhoto ? '10px' : '0'};
`;

const MessagePhotoContainer = styled.div`
  margin-top: 10px;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
`;

const MessagePhoto = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const PhotoModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const PhotoModalContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  cursor: default;
`;

const FullSizePhoto = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: -15px;
  right: -15px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }
`;

const ActionButtons = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

const ActionButton = styled.button`
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: black;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    transform: translateY(-1px);
  }

  &.edit-button {
    &:hover {
      background-color: #0bb5df;
    }
  }

  &.delete-button {
    &:hover {
      background-color: #FFCB98;
        
      span {
        div {
          color: #ffffff !important;
        }
      }
    }
  }
`;

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: ${getResponsiveValue({
    mobile: '42px',
    tablet: '55px',
    laptop: '55px',
    desktop: '55px'
  })};
`;

const EditInput = styled.textarea`
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-family: 'Computer Says No';
  font-weight: 400;
  font-size: 14px;
  padding: 10px;
  min-height: 60px;
  resize: vertical;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #0bb5df;
    box-shadow: 0 0 0 2px rgba(11, 181, 223, 0.2);
  }
`;

const EditButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-family: 'Computer Says No';
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const SaveButton = styled(Button)`
  background-color: #0bb5df;
  color: white;
  
  &:hover {
    background-color: #0a9bc1;
  }
`;

const CancelButton = styled(Button)`
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const EditIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const DeleteIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  font-weight: bold;
`;